/**
 * Reports (docs/specs/014): the shapes of `GET /api/reports/*`. Money in cents. The WhatsApp
 * tab's shape is in `modules/whatsapp-costs/contract.ts` (spec 027).
 */

export interface ReportFilters {
  from: string;
  to: string;
  unitId?: string;
  teamId?: string;
  ownerMembershipId?: string;
  pipelineId?: string;
  /** A number of the official WhatsApp (the WhatsApp tab, spec 027). */
  connectionId?: string;
  bucket?: ReportBucket;
}

export const REPORT_BUCKETS = ['day', 'week', 'month'] as const;
export type ReportBucket = (typeof REPORT_BUCKETS)[number];

export interface ReportPeriod {
  from: string;
  to: string;
  days: number;
  bucket: ReportBucket;
  timeZone: string;
}

export interface SalesTotals {
  created: number;
  won: number;
  wonValueCents: number;
  lost: number;
  lostValueCents: number;
  conversion: number | null;
  averageTicketCents: number | null;
  cycleDays: number | null;
}

export interface SalesReport {
  period: ReportPeriod;
  /** The same number of days right before: what the deltas compare against. */
  previousPeriod: ReportPeriod;
  totals: SalesTotals;
  previous: SalesTotals;
  /** The open deals now (not the period). */
  open: {
    deals: number;
    valueCents: number;
    rotting: number;
    withoutTask: number;
    overdueClose: number;
  };
  funnel: {
    pipeline: { id: string; name: string } | null;
    stages: {
      stageId: string;
      name: string;
      type: 'open' | 'won' | 'lost';
      entered: number;
      avgSecondsInStage: number | null;
      toNext: number | null;
      openDeals: number;
      openValueCents: number;
    }[];
  };
  lostReasons: { reasonId: string | null; label: string; deals: number; valueCents: number }[];
  owners: {
    membershipId: string | null;
    name: string | null;
    created: number;
    won: number;
    wonValueCents: number;
    lost: number;
    conversion: number | null;
  }[];
  series: { date: string; created: number; won: number; valueCents: number; lost: number }[];
  /**
   * Won in the period by catalog category (QA-04): the deals with a line of the category and
   * the total of those lines. `null` = lines without a catalog item or without a category.
   */
  categories: { categoryId: string | null; name: string | null; won: number; valueCents: number }[];
}

export interface ServiceRow {
  id: string | null;
  name: string | null;
  conversations: number;
  medianFirstResponseSeconds: number | null;
  /** Spec 045: resolution time of the cycles resolved (paused time discounted). */
  medianResolutionSeconds: number | null;
  resolved: number;
  slaMet: number | null;
  messagesSent?: number;
}

export interface ServiceTotals {
  conversations: number;
  unanswered: number;
  medianFirstResponseSeconds: number | null;
  avgFirstResponseSeconds: number | null;
  slaMet: number | null;
  slaEligible: number;
  slaBreached: number;
  messagesReceived: number;
  messagesSent: number;
  /** Spec 045: resolution time, reopening and first-contact resolution. */
  resolved: number;
  medianResolutionSeconds: number | null;
  avgResolutionSeconds: number | null;
  /** Resolves of the period, and how many were followed by a new cycle within 7 days. */
  resolutions: number;
  reopened: number;
  reopenRate: number | null;
  /** Conversations started in the period, and how many were resolved at the first contact. */
  started: number;
  firstContactResolved: number;
  firstContactResolution: number | null;
}

export const RESPONSE_BANDS = ['b5m', 'b15m', 'b1h', 'b4h', 'more'] as const;

/** Backlog bands by the age of the current cycle (spec 045, rule 16). */
export const BACKLOG_BANDS = ['b4h', 'b24h', 'b3d', 'more'] as const;
export type BacklogCounts = Record<(typeof BACKLOG_BANDS)[number], number>;

export interface ServiceBacklog {
  total: BacklogCounts;
  queues: ({ id: string | null; name: string | null } & BacklogCounts)[];
}

/** The tickets of a period (spec 056, D4). */
export interface TicketTotals {
  opened: number;
  resolved: number;
  canceled: number;
  reopened: number;
  /** Reopenings over the tickets resolved in the period; null without resolved ones. */
  reopenRate: number | null;
  medianResolutionSeconds: number | null;
}

/** The tickets of a contact reason resolved in the period. `null` = resolved without a reason. */
export interface TicketReasonRow {
  reasonId: string | null;
  label: string | null;
  parent: string | null;
  resolved: number;
  medianResolutionSeconds: number | null;
}

export const TICKET_AGE_BANDS = ['0-1d', '1-3d', '3-7d', '7d+'] as const;

/** Relatórios › Tickets (spec 056, D4). */
export interface TicketsReport {
  period: ReportPeriod;
  previousPeriod: ReportPeriod;
  totals: TicketTotals;
  previous: TicketTotals;
  /** The tickets in work today, whatever the period. */
  now: {
    open: number;
    pending: number;
    onHold: number;
    overdue: number;
    ages: { band: (typeof TICKET_AGE_BANDS)[number]; count: number }[];
  };
  assignees: {
    membershipId: string | null;
    name: string | null;
    inWork: number;
    resolved: number;
    medianResolutionSeconds: number | null;
  }[];
  reasons: TicketReasonRow[];
}

export interface ServiceReport {
  period: ReportPeriod;
  previousPeriod: ReportPeriod;
  totals: ServiceTotals;
  previous: ServiceTotals;
  queues: ServiceRow[];
  agents: ServiceRow[];
  /**
   * The resolves in the period by the outcome chosen (QA-31); a conversation resolved twice
   * counts twice. `null` = resolved without an outcome.
   */
  outcomes: { outcomeId: string | null; label: string | null; resolved: number }[];
  series: {
    date: string;
    conversations: number;
    medianFirstResponseSeconds: number | null;
    slaBreached: number;
    messagesReceived: number;
  }[];
  responseBands: { band: (typeof RESPONSE_BANDS)[number]; conversations: number }[];
  /** Conversations started per ISO weekday (1 = Monday) and local hour; empty cells absent. */
  heatmap: { weekday: number; hour: number; conversations: number }[];
  /** Spec 045: the open and pending conversations now, by age; not bound to the period. */
  backlog: ServiceBacklog;
  /** Spec 047: the satisfaction surveys sent in the period (absent on an older API). */
  satisfaction?: ServiceSatisfaction;
  /** Spec 050: the NPS questions sent by broadcasts in the period (absent on an older API). */
  nps?: ServiceNps;
}

export interface ServiceNps {
  sent: number;
  answered: number;
  /** % promoters − % detractors; null with no answer. */
  score: number | null;
  promoters: number;
  passives: number;
  detractors: number;
  responseRate: number | null;
  /** Answers by score, index 0 to 10. */
  distribution: number[];
  comments: {
    id: string;
    score: number;
    comment: string;
    answeredAt: string | null;
    customer: string | null;
  }[];
}

export interface ServiceSatisfaction {
  sent: number;
  answered: number;
  /** Mean score of the answered ones, null without answers. */
  average: number | null;
  /** How many answered 1, 2, 3, 4 and 5. */
  distribution: number[];
  /** Answered over sent, 0 to 1. */
  responseRate: number | null;
  agents: { id: string | null; name: string | null; answered: number; average: number | null }[];
  queues: { id: string | null; name: string | null; answered: number; average: number | null }[];
  comments: {
    id: string;
    score: number;
    comment: string;
    answeredAt: string | null;
    protocol: string;
    member: string | null;
  }[];
}

export interface SourcesTotals {
  customers: number;
  created: number;
  won: number;
  wonValueCents: number;
  conversion: number | null;
  leads: number;
  newCustomers: number;
}

export interface SourcesReport {
  period: ReportPeriod;
  previousPeriod: ReportPeriod;
  totals: SourcesTotals;
  previous: SourcesTotals;
  series: { date: string; leads: number; newCustomers: number }[];
  origins: {
    origin: string;
    customers: number;
    created: number;
    won: number;
    wonValueCents: number;
    lost: number;
    conversion: number | null;
  }[];
  leads: {
    total: number;
    newCustomers: number;
    bySource: {
      sourceId: string | null;
      source: string | null;
      via: string;
      leads: number;
      newCustomers: number;
      withDeal: number;
    }[];
  };
}

export interface AutomationsTotals {
  runs: number;
  succeeded: number;
  failed: number;
  running: number;
  stopped: number;
  successRate: number | null;
}

export interface AutomationsReport {
  period: ReportPeriod;
  previousPeriod: ReportPeriod;
  totals: AutomationsTotals;
  previous: AutomationsTotals;
  series: { date: string; runs: number; succeeded: number; failed: number }[];
  automations: {
    automationId: string;
    name: string;
    kind: string;
    runs: number;
    succeeded: number;
    failed: number;
    running: number;
    stopped: number;
    skipped: number;
  }[];
}

export const REPORT_TABS = [
  'sales',
  'service',
  'tickets',
  'sources',
  'automations',
  'whatsapp',
] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

export const PERIOD_PRESETS = [
  '7d',
  '30d',
  '90d',
  'month',
  'lastMonth',
  'year',
  '12m',
  'custom',
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];
